<template>
  <div class="sale-page returned-sale-page">
    <block-buttons
      title="Возвраты продаж"
      description="Возвраты сотрудника за выбранный период"
      :actions="toolbarActions"
    >
      <template #icon>
        <ShoppingBag :size="22" />
      </template>
    </block-buttons>

    <form
      class="sale-page__filters"
      @submit.prevent="loadDataWrapper"
    >
      <label>
        Сотрудник
        <select
          v-model="selectedUser"
          :disabled="loading"
          required
          @change="clearData"
        >
          <option value="">Действия</option>

          <option
            v-for="user in users"
            :key="user.id"
            :value="user.id"
          >
            {{ `${user.name} ${user.surname}` }}
          </option>
        </select>
      </label>

      <label>
        Период

        <select
          v-model="period"
          :disabled="loading"
          @change="selectPeriod"
        >
          <option value="1">За месяц</option>
          <option value="3">За 3 месяца</option>
          <option value="6">За 6 месяцев</option>
          <option value="12">За год</option>
          <option value="custom">Произвольный период</option>
        </select>
      </label>

      <label>
        С
        <input
          v-model="dateFrom"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <label>
        По
        <input
          v-model="dateTo"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <button
        type="submit"
        :disabled="loading || !selectedUser"
      >
        Показать
      </button>
    </form>

    <data-table
      :rows="renderData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      :search-keys="['productListName', 'productCodes', 'typePay', 'price', 'createdAtFormatted']"
      title="Возвращённые продажи"
      description="Возвраты сотрудника за выбранный период"
      search-placeholder="Товар, код, способ оплаты или дата"
      :empty-text="emptyText"
      @retry="loadDataWrapper"
    >
      <template #header>
        <span class="sale-page__count">Всего продаж: {{ sales.length }}</span>
      </template>

      <template #cell-productListName="{ row }">
        <strong>{{ row.productListName }}</strong>
        <small
          v-if="row.isReturned"
          class="sale-page__returned"
        >
          Возврат
        </small>
      </template>

      <template #cell-price="{ row }">
        <strong>{{ priceToFormat(row.price) }}</strong>
      </template>

      <template #cell-createdAt="{ row }">{{ row.createdAtFormatted }}</template>

      <template #cell-isReturned>Возврат</template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с продажей ${row.id}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ShoppingBag, RefreshCw } from 'lucide-vue'
import moment from 'moment'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'
import { priceToFormat, formatDate } from '@/helper'

export default {
  name: 'ReturnedSaleV2',
  components: {
    ShoppingBag,
    BlockButtons,
    DataTable
  },
  mixins: [permissions],
  data () {
    return {
      sales: [],
      selectedUser: '',
      loading: false,
      error: '',
      period: '1',
      dateFrom: moment().startOf('month').format('DD.MM.YYYY'),
      dateTo: moment().format('DD.MM.YYYY')
    }
  },
  computed: {
    ...mapGetters('users', ['getActiveUsers']),
    ...mapGetters('user', {
      isSeller: 'isSeller',
      currentUser: 'getData'
    }),
    ...mapGetters('product', ['getDataObject']),
    ...mapGetters('settings', ['getDefaultValues', 'getTableColumns']),
    users () {
      return this.isSeller
        ? [this.currentUser]
        : this.getActiveUsers || []
    },
    renderData () {
      const paymentTypes = this.getDefaultValues?.['type-pay'] || []

      return this.sales.map(sale => ({
        ...sale,
        createdAtFormatted: formatDate(sale.createdAt),
        productListName: sale.productList.map(id => this.getDataObject[id]?.title).filter(Boolean).join(' '),
        productCodes: (sale.productList || []).map(id => this.getDataObject[id]?.code).filter(Boolean).join(' '),
        typePayText: paymentTypes.find(({ code }) => code === sale.typePay)?.name,
        renderPrice: priceToFormat(sale.price),
        renderPurchasePrice: priceToFormat(sale.purchasePrice)
      }))
    },
    toolbarActions () {
      return  [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: this.loadDataWrapper
        }
      ]
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['sale'] || [])
      ]
    },
    emptyText () {
      if (!this.selectedUser) {
        return 'Выберите сотрудника'
      }

      return 'Возвратов за выбранный период нет'
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'sale')
        }
      ].filter(({ visible }) => visible)
    }
  },
  watch: {
    currentUser: {
      immediate: true,
      async handler (value) {
        this.selectedUser = value.id

        await this.$nextTick()
        await this.loadDataWrapper()
      }
    }
  },
  methods: {
    ...mapActions('sale', ['getReturnedSales', 'remove']),
    ...mapActions('modal', ['openModal']),
    priceToFormat,
    formatDate,
    async loadDataWrapper () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      const { data, error } = await this.getReturnedSales({
        userId: this.selectedUser,
        dateRange: [this.dateFrom, this.dateTo]
      })

      this.loading = false
      this.$bus.emit('stop-loader')

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при загрузке данных!',
          text: 'Произошла ошибка при загрузке возвратов. Повторите попытку позже!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.sales = data
    },
    clearData () {
      this.error = ''
    },
    selectPeriod () {
      if (this.period === 'custom') {
        return
      }

      this.dateFrom = moment().subtract(Number(this.period), 'month').format('DD.MM.YYYY')
      this.dateTo = moment().format('DD.MM.YYYY')

      this.loadDataWrapper()
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction(action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'sale',
              moduleAction: 'sale',
              type: 'info',
              title: `Информация - ${row.name} ${row.surname}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'remove':
          this.removeSale(row)
      }
    },
    async removeSale ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении продажи!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.loadDataWrapper()

      this.$notify({
        type: 'success',
        title: 'Продажа успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    },
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
